import { getPeriodForDate } from '@/lib/utils/periods';

/**
 * Period bookkeeping for clinical exams. Pure — no Firebase.
 *
 * canTakeExam / getCurrentPeriodExam locate a customer's exam by `periodId`,
 * so every write MUST carry it; saveClinicalExam did not, which left all 95
 * production exams invisible to the dialog. The period is derived from the
 * exam's own date so a back-dated exam lands in the quarter it belongs to.
 */
export function withPeriodId<T extends { date: Date; periodId?: string }>(
  params: T,
): T & { periodId: string } {
  return { ...params, periodId: params.periodId || getPeriodForDate(params.date).id };
}

type MillisLike = Date | { toMillis: () => number } | null | undefined;

function toMillis(v: MillisLike): number {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  return typeof v.toMillis === 'function' ? v.toMillis() : 0;
}

/**
 * When a customer has more than one exam in a period (the duplicate documents
 * the missing periodId caused), the dialog must load the same one every time:
 * the most recently updated, falling back to the exam date.
 */
export function pickLatestExam<T extends { updatedAt?: MillisLike; date?: MillisLike }>(
  exams: T[],
): T | undefined {
  let best: T | undefined;
  let bestAt = -1;
  for (const exam of exams) {
    const at = toMillis(exam.updatedAt) || toMillis(exam.date);
    if (at > bestAt) {
      best = exam;
      bestAt = at;
    }
  }
  return best;
}
